import { Modal, Text, Image, View, TouchableOpacity } from "react-native";
import React, { useEffect, useState, useRef } from "react";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { CreateResponsiveStyle, DEVICE_SIZES } from "rn-responsive-styles";
import { AppImages } from "../../../../utility/AppImages";
import SponsorSection from "../../../../Components/live-streaming-common-components/SponsorSection";

interface GetReadyModalProps {
  visible: boolean;
  onClose: () => void;
  onCountdownComplete: () => void;
  countdownSeconds?: number;
}

export default function GetReadyModal({
  visible,
  onClose,
  onCountdownComplete,
  countdownSeconds = 10,
}: GetReadyModalProps) {
  const styles = useStyles();
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCompletedRef = useRef(false);

  useEffect(() => {
    // Reset and cleanup when modal is hidden
    if (!visible) {
      setProgress(0);
      isCompletedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Prevent multiple intervals
    if (intervalRef.current) {
      return;
    }

    const totalDuration = Math.max(1, countdownSeconds) * 1000; // ms
    const intervalMs = 100; // Update every 100ms
    const steps = totalDuration / intervalMs;
    const increment = 100 / steps; // Progress increment per step

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + increment;
        if (newProgress >= 100 && !isCompletedRef.current) {
          isCompletedRef.current = true;
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          setTimeout(() => onCountdownComplete(), 300);
          return 100;
        }
        return newProgress;
      });
    }, intervalMs);

    // Cleanup on unmount or visibility change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [visible, countdownSeconds, onCountdownComplete]);

  if (!visible) return null;

  return (
    <Modal transparent={true} visible={visible} animationType="fade">
      <View style={styles.container}>
        <View style={styles.mainView}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Image
              source={AppImages.icCancel}
              style={styles.closeIcon}
              resizeMode="contain"
            />
          </TouchableOpacity>

          <View style={styles.contentView}>
            <Text style={styles.titleText}>Get Ready...</Text>
            <Text style={styles.subtitleText}>Event is about to go live</Text>

            {/* Sponsor Section */}
            <View style={styles.sponsorSection}>
              <Text style={styles.sponsorText}>Sponsored by</Text>
              <SponsorSection />
            </View>

            {/* Progress Bar */}
            <View style={styles.progressContainer}>
              <View style={styles.progressBarBackground}>
                <View style={[styles.progressBar, { width: `${progress}%` }]} />
              </View>
              <Text style={styles.progressText}>{Math.round(progress)}%</Text>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* Styles */
const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
      justifyContent: "center",
      alignItems: "center",
    },
    mainView: {
      borderRadius: 8,
      borderWidth: 1.6,
      borderColor: "rgba(239, 68, 68, 0.50)",
      padding: 24,
      width: "85%",
      maxWidth: 600,
      alignItems: "center",
      position: "relative",
      backgroundColor: "#111827",
    },
    closeButton: {
      position: "absolute",
      top: 12,
      right: 12,
      backgroundColor: "rgba(57, 57, 57, 0.8)",
      width: 24,
      height: 24,
      borderRadius: 12,
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1,
    },
    closeIcon: {
      height: 10,
      width: 10,
      tintColor: AppColors.white,
    },
    contentView: {
      alignItems: "center",
      width: "100%",
      paddingTop: 10,
    },
    titleText: {
      ...AppFontStyle.BOLD_ANYBODY_28,
      color: AppColors.white,
      textAlign: "center",
      marginBottom: 8,
    },
    subtitleText: {
      ...AppFontStyle.REGULAR_ANYBODY_14,
      color: AppColors.gray_C3C3C3,
      textAlign: "center",
      marginBottom: 20,
    },
    sponsorSection: {
      width: "100%",
      marginBottom: 24,
    },
    sponsorText: {
      ...AppFontStyle.REGULAR_ANYBODY_12,
      color: AppColors.white,
      marginBottom: 8,
      textAlign: "center",
    },
    progressContainer: {
      width: "100%",
      alignItems: "center",
    },
    progressBarBackground: {
      width: "100%",
      height: 6,
      backgroundColor: "rgba(57, 57, 57, 0.8)",
      borderRadius: 3,
      overflow: "hidden",
      marginBottom: 8,
    },
    progressBar: {
      height: "100%",
      backgroundColor: "#EF4444",
      borderRadius: 3,
    },
    progressText: {
      ...AppFontStyle.BOLD_ANYBODY_12,
      color: AppColors.white,
    },
  },
  {
    [DEVICE_SIZES.XS]: {
      mainView: {
        padding: 16,
        width: "95%",
      },
      titleText: {
        ...AppFontStyle.BOLD_ANYBODY_20,
      },
      subtitleText: {
        ...AppFontStyle.REGULAR_ANYBODY_12,
      },
      sponsorText: {
        ...AppFontStyle.REGULAR_ANYBODY_10,
      },
      progressText: {
        ...AppFontStyle.BOLD_ANYBODY_10,
      },
    },
    [DEVICE_SIZES.SM]: {
      mainView: {
        padding: 20,
        width: "90%",
      },
      titleText: {
        ...AppFontStyle.BOLD_ANYBODY_24,
      },
    },
    [DEVICE_SIZES.MD]: {
      mainView: {
        width: "75%",
        maxWidth: 550,
      },
    },
    [DEVICE_SIZES.LG]: {
      mainView: {
        width: "70%",
        maxWidth: 600,
      },
    },
  }
);
